<!-- Step 1: Upload CSV & Get JSON -->
<!-- Step 2: Review Data & Correct Issues -->
<!-- Step 3: Start Uploading Batch -->

<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Batch Upload" />

    <div class="container">
      <div class="mb-6 card">
        <div class="flex justify-between pb-6 text-lg font-bold card-header">
          Upload CSV File
          <div>
            <a href="https://api.fleetbeacon.app/files/batch-template.csv" download="batch-template.csv" class="mr-3 btn-sm btn btn-outline">
              <fa :icon="['far', 'arrow-down']" class="mr-3 opacity-75" /> Download Template
            </a>
            <button class="btn-sm btn btn-secondary" @click="reset">
              <fa :icon="['far', 'redo']" class="mr-3 opacity-75" /> Reset
            </button>
          </div>
        </div>

        <!-- Upload -->
        <Collapse :active="rows.length < 1" :duration="200">
          <div class="border-t rounded-b-lg bg-gray-50">
            <form ref="csvForm" class="max-w-sm py-12 mx-auto text-center sm:flex" @submit.prevent="uploadCSV">
              <label for="csv" class="w-full">
                <input id="csv" type="file" class="form-input" @input="loadFile">
              </label>
              <button type="submit" class="w-full mt-2 btn btn-primary sm:mt-0 sm:ml-2 sm:w-auto" v-submitting="submitting">
                Upload
              </button>
            </form>
          </div>
        </Collapse>
        <!-- Loading -->
        <Collapse :active="submitting" :duration="200" class="p-12">
          <Loading />
        </Collapse>
        <!-- Rows -->
        <Collapse :active="rows.length > 0" :duration="200">
          <div class="flex items-center justify-between p-4 border-t border-b bg-gray-50">
            <div>
              <div class="font-bold">{{ rows.length }} Orders to Upload</div>
              <div class="flex text-gray-500">
                {{ successRows }}/{{ rows.length }} Successful 
                <div class="mx-2">&bullet;</div> 
                {{ errorRows }} Errors
              </div>
            </div>
            <button class="btn btn-primary" @click="uploadRows">
              Start Batch
            </button>
          </div>
          <div class="divide-y">
            <div v-for="(row, index) in rows" :key="`row-${index}`" :ref="`row-${index}`" class="hover:bg-gray-50">
              <div class="grid items-center grid-cols-12 gap-4 p-4">
                <div class="col-span-1 text-xl text-center">
                  <Loading v-if="row.status === 'loading'" />
                  <fa :icon="['far', 'clock']" class="text-gray-400" v-if="row.status === 'waiting'" />
                  <fa :icon="['far', 'times-circle']" class="text-red-500" v-if="row.status === 'error'" />
                  <fa :icon="['far', 'check-circle']" class="text-emerald-500" v-if="row.status === 'success'" />
                </div>

                <div class="col-span-2">
                  <div class="font-bold">
                    {{ parseOrderType(row.ordertype) }}
                  </div>
                  <div>
                    {{ row.orderdate }}
                  </div>
                </div>

                <div class="col-span-3 truncate whitespace-nowrap">
                  <div class="w-full truncate">
                    <span class="text-gray-500">Customer:</span> {{ row.customer_name }}
                  </div>
                  <div class="w-full truncate">
                    <span class="text-gray-500">Contact:</span> {{ row.contact_name }}
                  </div>
                </div>

                <div class="col-span-3 truncate whitespace-nowrap">
                  <div class="w-full truncate">
                    {{ row.formatted_address }}
                  </div>
                  <div class="w-full text-gray-500 truncate">
                    {{ row.orderid }} &bullet; {{ row.ordertext }}
                  </div>
                </div>

                <div class="col-span-3 text-right">
                  <button class="mr-2 btn btn-sm btn-secondary" @click="row.open = !row.open" v-if="row.status !== 'success'">
                    {{ row.open ? 'Close' : 'Edit' }}
                  </button>
                  <button class="btn btn-sm btn-secondary" v-if="row.status === 'error'" @click="uploadRow(row, index)">
                    Retry
                  </button>
                </div>
              </div>
              <Collapse :active="row.status !== 'success' && row.open" :duration="300">
                <div v-if="row.error" class="p-4">
                  <div class="p-4 text-red-700 bg-red-200 rounded-md">
                    <strong>Error:</strong> {{ row.error }}
                  </div>
                </div>
                <div class="grid grid-cols-1 p-4 bg-gray-50 lg:grid-cols-3 gap-x-4 gap-y-4" v-if="row.open">
                  <div>
                    <label class="form-label">Order Number</label>
                    <input type="text" class="form-input" v-model="row.orderid">
                    <validations :errors="row.errors" field="orderid" />
                  </div>
                  <div>
                    <label class="form-label">Order Date</label>
                    <input type="text" class="form-input" v-model="row.orderdate">
                    <validations :errors="row.errors" field="orderdate" />
                  </div>
                  <div>
                    <label class="form-label">Order Type</label>
                    <radio-group 
                      v-model="row.ordertype"
                      :options="[
                        { value: 1, label: 'Service' },
                        { value: 2, label: 'Pickup' },
                        { value: 3, label: 'Delivery' }
                      ]" />
                    <validations :errors="row.errors" field="ordertype" />
                  </div>
                  <div>
                    <label class="form-label">Customer Name</label>
                    <input type="text" class="form-input" v-model="row.customer_name">
                    <validations :errors="row.errors" field="customer_name" />
                  </div>
                  <div>
                    <label class="form-label">Customer Phone</label>
                    <input type="text" class="form-input" v-model="row.customer_phone">
                    <validations :errors="row.errors" field="customer_phone" />
                  </div>
                  <div>
                    <label class="form-label">Customer Email</label>
                    <input type="text" class="form-input" v-model="row.customer_email">
                    <validations :errors="row.errors" field="customer_email" />
                  </div>
                  <div>
                    <label class="form-label">Contact Name</label>
                    <input type="text" class="form-input" v-model="row.contact_name">
                    <validations :errors="row.errors" field="contact_name" />
                  </div>
                  <div>
                    <label class="form-label">Contact Phone</label>
                    <input type="text" class="form-input" v-model="row.contact_phone">
                    <validations :errors="row.errors" field="contact_phone" />
                  </div>
                  <div>
                    <label class="form-label">Contact Email</label>
                    <input type="text" class="form-input" v-model="row.contact_email">
                    <validations :errors="row.errors" field="contact_email" />
                  </div>
                  <div class="col-span-3">
                    <label class="form-label">Address</label>
                    <input type="text" class="form-input" v-model="row.formatted_address">
                    <validations :errors="row.errors" field="formatted_address" />
                  </div>
                  <div class="col-span-3">
                    <label class="form-label">Notes</label>
                    <input type="text" class="form-input" v-model="row.ordertext">
                    <validations :errors="row.errors" field="ordertext" />
                  </div>
                  <div class="col-span-3">
                    <label class="form-label">Reference</label>
                    <input type="text" class="form-input" v-model="row.reference">
                    <validations :errors="row.errors" field="reference" />
                  </div>
                </div>
              </Collapse>
            </div>
          </div>
        </Collapse>
      </div>
      
    </div>
  </div>
</template>

<script>
export default {
  name: 'BatchUpload',

  metaInfo: {
    title: 'Batch Upload'
  },

  computed: {
    successRows () {
      if (this.rows) {
        return this.rows.filter(row => row.status === 'success').length
      } else {
        return 0
      }
    },

    errorRows () {
      if (this.rows) {
        return this.rows.filter(row => row.status === 'error').length
      } else {
        return 0
      }
    }
  },

  data () {
    return {
      submitting: false,
      csv: null,
      rows: []
    }
  },

  methods: {
    parseOrderType (code) {
      const typeEnum = {
        '1': 'Service',
        '2': 'Pickup',
        '3': 'Delivery'
      }
      return typeEnum[code.toString()]
    },

    reset () {
      this.csv = null
      this.rows = []
      this.$refs.csvForm.reset()
    },

    loadFile (event) {
      var files = event.target.files || event.dataTransfer.files
      this.csv = files[0]
    },

    async uploadCSV () {
      try {
        this.submitting = true
        const formData = new FormData()
        formData.append('csv', this.csv)
        const { data } = await this.$api.post(`account/${this.$route.params.account}/batch`, formData)
        this.rows = data.rows
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    },

    async uploadRows () {
      for (let i = 0; i < this.rows.length; i++) {
        if (this.rows[i].status !== 'success') {
          this.uploadRow(this.rows[i], i)
        }
      }
    },

    async uploadRow (row, index) {
      this.$set(this.rows[index], 'status', 'loading')
      try {
        const { data } = await this.$api.post(`account/${this.$route.params.account}/orders`, row)
        this.$set(this.rows[index], 'status', 'success')
        this.$set(this.rows[index], 'error', null)
      } catch (error) {
        console.error(error)
        this.$set(this.rows[index], 'status', 'error')
        this.$set(this.rows[index], 'error', error.response.data.message)
        this.$set(this.rows[index], 'errors', error.response.data.errors)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>